import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { Users, Mail, CalendarDays } from "lucide-react";

const TABS = [
  { key: "newsletters", label: "Newsletters (validation)", icon: Mail },
  { key: "actus", label: "Expos & actus", icon: CalendarDays },
  { key: "artists", label: "Artistes suivis", icon: Users }
];

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function formatDateFR(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("fr-FR");
  } catch {
    return String(value);
  }
}

export default function GalleryTrackingSection() {
  const { user } = useAuth();

  const [tab, setTab] = useState("newsletters");

  // galeries
  const [galleries, setGalleries] = useState([]);
  const [selectedGalleryId, setSelectedGalleryId] = useState("");
  const [loadingGalleries, setLoadingGalleries] = useState(false);

  // artistes suivis
  const [artists, setArtists] = useState([]);
  const [loadingArtists, setLoadingArtists] = useState(false);
  const [newArtistName, setNewArtistName] = useState("");
  const [newArtistNotes, setNewArtistNotes] = useState("");
  const [savingArtist, setSavingArtist] = useState(false);

  // actus / expos (GalerieActualites)
  const [actus, setActus] = useState([]);
  const [loadingActus, setLoadingActus] = useState(false);
  const [creatingActu, setCreatingActu] = useState(false);

  // mini formulaire
  const [actuType, setActuType] = useState("exposition");
  const [actuTitre, setActuTitre] = useState("");
  const [actuArtistes, setActuArtistes] = useState("");
  const [actuDateDebut, setActuDateDebut] = useState("");
  const [actuDateFin, setActuDateFin] = useState("");
  const [actuLieu, setActuLieu] = useState("");
  const [actuSourceUrl, setActuSourceUrl] = useState("");
  const [actuImageUrl, setActuImageUrl] = useState("");
  const [actuDescription, setActuDescription] = useState("");

  // load galeries
  useEffect(() => {
    async function loadGalleries() {
      try {
        setLoadingGalleries(true);
        const { data, error } = await supabase
          .from("Galeries")
          .select("id, nom, ville, pays")
          .order("nom", { ascending: true });
        if (error) throw error;
        setGalleries(data || []);
      } catch (e) {
        console.error("Erreur chargement galeries :", e);
      } finally {
        setLoadingGalleries(false);
      }
    }
    loadGalleries();
  }, []);

  const selectedGallery = useMemo(
    () => galleries.find((g) => String(g.id) === String(selectedGalleryId)),
    [galleries, selectedGalleryId]
  );

  // load artistes + actus quand galerie change
  useEffect(() => {
    if (!selectedGalleryId) {
      setArtists([]);
      setActus([]);
      return;
    }

    (async () => {
      // artistes
      try {
        setLoadingArtists(true);
        const { data, error } = await supabase
          .from("GalerieArtistes")
          .select("id, artist_name, source, confidence_score, is_verified, notes")
          .eq("gallery_id", selectedGalleryId)
          .order("artist_name", { ascending: true });
        if (error) throw error;
        setArtists(data || []);
      } catch (e) {
        console.error("Erreur chargement artistes :", e);
        setArtists([]);
      } finally {
        setLoadingArtists(false);
      }

      // actus
      await refreshActus(selectedGalleryId);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGalleryId]);

  async function refreshActus(galleryId = selectedGalleryId) {
    if (!galleryId) return;
    try {
      setLoadingActus(true);
      const { data, error } = await supabase
        .from("GalerieActualites")
        .select(
          "id, type, titre, description, artiste, artistes, date_debut, date_fin, source_url, image_url, created_at, is_obsolete"
        )
        .eq("gallery_id", galleryId)
        .order("date_debut", { ascending: false, nullsFirst: false })
        .limit(200);

      if (error) throw error;
      setActus(data || []);
    } catch (e) {
      console.error("Erreur chargement actus :", e);
      setActus([]);
    } finally {
      setLoadingActus(false);
    }
  }

  // artistes: add / verify / delete
  async function handleAddArtist() {
    if (!selectedGalleryId) return window.alert("Choisis d'abord une galerie.");
    if (!newArtistName.trim()) return window.alert("Nom de l'artiste requis.");

    try {
      setSavingArtist(true);

      const payload = {
        gallery_id: selectedGalleryId,
        artist_name: newArtistName.trim(),
        source: "team_manual",
        confidence_score: 0.9,
        notes: newArtistNotes?.trim() || null
      };

      const { error } = await supabase.from("GalerieArtistes").insert([payload]);
      if (error) throw error;

      setNewArtistName("");
      setNewArtistNotes("");

      const { data } = await supabase
        .from("GalerieArtistes")
        .select("id, artist_name, source, confidence_score, is_verified, notes")
        .eq("gallery_id", selectedGalleryId)
        .order("artist_name", { ascending: true });

      setArtists(data || []);
    } catch (e) {
      console.error("Erreur ajout artiste :", e);
      window.alert("Ajout artiste impossible : " + e.message);
    } finally {
      setSavingArtist(false);
    }
  }

  async function toggleVerifyArtist(artist) {
    try {
      const { error } = await supabase
        .from("GalerieArtistes")
        .update({
          is_verified: !artist.is_verified,
          verified_by: user?.id || null,
          verified_at: !artist.is_verified ? new Date().toISOString() : null
        })
        .eq("id", artist.id);

      if (error) throw error;

      setArtists((prev) =>
        prev.map((a) =>
          a.id === artist.id ? { ...a, is_verified: !artist.is_verified } : a
        )
      );
    } catch (e) {
      console.error("Erreur verify artiste :", e);
      window.alert("Impossible de mettre à jour : " + e.message);
    }
  }

  async function deleteArtist(id) {
    if (!window.confirm("Supprimer cet artiste suivi pour cette galerie ?")) return;
    try {
      const { error } = await supabase.from("GalerieArtistes").delete().eq("id", id);
      if (error) throw error;
      setArtists((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      console.error("Erreur suppression artiste :", e);
      window.alert("Suppression impossible : " + e.message);
    }
  }

  // actus: create
  async function handleCreateActu() {
    if (!selectedGalleryId) return window.alert("Choisis d'abord une galerie.");
    if (!actuTitre.trim()) return window.alert("Titre requis.");

    try {
      setCreatingActu(true);

      const descriptionParts = [];
      if (actuLieu?.trim()) descriptionParts.push(`📍 ${actuLieu.trim()}`);
      if (actuDescription?.trim()) descriptionParts.push(actuDescription.trim());

      const payload = {
        gallery_id: selectedGalleryId,
        type: actuType || null,
        titre: actuTitre.trim(),
        description: descriptionParts.length ? descriptionParts.join("\n\n") : null,
        artistes: actuArtistes?.trim() || null,
        artiste: null,
        date_debut: actuDateDebut || null,
        date_fin: actuDateFin || null,
        source_url: actuSourceUrl?.trim() || null,
        image_url: actuImageUrl?.trim() || null,
        created_by: user?.id || null
      };

      const { error } = await supabase.from("GalerieActualites").insert([payload]);
      if (error) throw error;

      // reset
      setActuType("exposition");
      setActuTitre("");
      setActuArtistes("");
      setActuDateDebut("");
      setActuDateFin("");
      setActuLieu("");
      setActuSourceUrl("");
      setActuImageUrl("");
      setActuDescription("");

      await refreshActus();
    } catch (e) {
      console.error("create actu error:", e);
      window.alert("Impossible de créer l'actu : " + e.message);
    } finally {
      setCreatingActu(false);
    }
  }

  return (
    <div className="border border-gray-200 rounded-2xl p-6 bg-white">
      {/* Galerie selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Galerie</label>
        <select
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          value={selectedGalleryId}
          onChange={(e) => setSelectedGalleryId(e.target.value)}
          disabled={loadingGalleries}
        >
          <option value="">
            {loadingGalleries ? "Chargement…" : "Sélectionner une galerie…"}
          </option>
          {galleries.map((g) => (
            <option key={g.id} value={g.id}>
              {g.nom} {g.ville ? `— ${g.ville}` : ""} {g.pays ? `(${g.pays})` : ""}
            </option>
          ))}
        </select>
        {selectedGallery && (
          <p className="mt-1 text-xs text-gray-500">
            Suivi pour : <span className="font-medium">{selectedGallery.nom}</span>
          </p>
        )}
      </div>

      {!selectedGalleryId ? (
        <p className="text-sm text-gray-500">
          Choisis une galerie pour gérer les actus et les artistes suivis.
        </p>
      ) : (
        <>
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={classNames(
                    "inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border",
                    active
                      ? "bg-black text-white border-black"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* TAB newsletters: pas d’upload ici (évite doublon) */}
          {tab === "newsletters" && (
            <div className="border border-gray-200 rounded-xl p-6 bg-gray-50">
              <h3 className="font-medium text-gray-900 mb-2">Newsletters (PDF)</h3>
              <p className="text-sm text-gray-700">
                L’import et la validation des newsletters se font dans la section{" "}
                <span className="font-medium">📨 Newsletters</span> du TeamDashboard.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                (On évite ainsi deux systèmes d’upload et deux buckets.)
              </p>
            </div>
          )}

          {/* TAB actus */}
          {tab === "actus" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Form create */}
              <div className="lg:col-span-1 border border-gray-200 rounded-xl p-4 bg-gray-50">
                <h3 className="font-medium text-gray-900 mb-3">Ajouter une expo / actu</h3>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Type</label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={actuType}
                      onChange={(e) => setActuType(e.target.value)}
                    >
                      <option value="exposition">Exposition</option>
                      <option value="opening">Vernissage / Opening</option>
                      <option value="event">Événement</option>
                      <option value="publication">Publication</option>
                      <option value="foire">Foire</option>
                      <option value="prix">Prix / Distinction</option>
                      <option value="acquisition">Acquisition musée</option>
                      <option value="record">Record enchères</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Titre *</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="Ex: Claire Tabouret @ Grand Palais"
                      value={actuTitre}
                      onChange={(e) => setActuTitre(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Artistes</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="Ex: Claire Tabouret; …"
                      value={actuArtistes}
                      onChange={(e) => setActuArtistes(e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Début</label>
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        value={actuDateDebut}
                        onChange={(e) => setActuDateDebut(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Fin</label>
                      <input
                        type="date"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        value={actuDateFin}
                        onChange={(e) => setActuDateFin(e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Lieu</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="Ex: Paris, Grand Palais"
                      value={actuLieu}
                      onChange={(e) => setActuLieu(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Lien (source_url)</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="https://..."
                      value={actuSourceUrl}
                      onChange={(e) => setActuSourceUrl(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Image (image_url)</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="https://..."
                      value={actuImageUrl}
                      onChange={(e) => setActuImageUrl(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Description</label>
                    <textarea
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      rows={4}
                      placeholder="Infos utiles…"
                      value={actuDescription}
                      onChange={(e) => setActuDescription(e.target.value)}
                    />
                  </div>

                  <button
                    onClick={handleCreateActu}
                    disabled={creatingActu}
                    className="w-full px-4 py-2 rounded-lg bg-black text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
                  >
                    {creatingActu ? "Création…" : "Ajouter l’actu"}
                  </button>
                </div>
              </div>

              {/* List */}
              <div className="lg:col-span-2 border border-gray-200 rounded-xl p-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900">Expos & actus</h3>
                  <button
                    onClick={() => refreshActus()}
                    className="text-[11px] px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                  >
                    Rafraîchir
                  </button>
                </div>

                <div className="max-h-[520px] overflow-y-auto">
                  {loadingActus ? (
                    <p className="text-xs text-gray-500">Chargement…</p>
                  ) : actus.length === 0 ? (
                    <p className="text-xs text-gray-500">Aucune actu/expo enregistrée.</p>
                  ) : (
                    <ul className="space-y-2">
                      {actus.map((a) => (
                        <li
                          key={a.id}
                          className="border border-gray-200 rounded-lg p-3 flex items-start justify-between gap-3"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                                {a.type || "—"}
                              </span>
                              {a.is_obsolete && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                                  Obsolète
                                </span>
                              )}
                            </div>

                            <div className="font-medium text-sm mt-1">
                              {a.titre || "(sans titre)"}
                            </div>

                            <div className="text-[11px] text-gray-500 mt-1">
                              {a.date_debut ? formatDateFR(a.date_debut) : "—"}
                              {a.date_fin ? ` → ${formatDateFR(a.date_fin)}` : ""}
                              {" • "}
                              Artistes: {a.artistes || a.artiste || "—"}
                            </div>

                            {a.description && (
                              <div className="text-[11px] text-gray-600 mt-2 whitespace-pre-line">
                                {a.description}
                              </div>
                            )}

                            {a.source_url && (
                              <div className="text-[11px] text-gray-500 mt-2">
                                Source:{" "}
                                <a
                                  href={a.source_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="underline"
                                >
                                  ouvrir
                                </a>
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB artists */}
          {tab === "artists" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Add artist */}
              <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                <h3 className="font-medium text-gray-900 mb-3">Ajouter un artiste suivi</h3>

                <div className="space-y-3">
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Nom de l'artiste…"
                    value={newArtistName}
                    onChange={(e) => setNewArtistName(e.target.value)}
                  />
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Notes internes (optionnel)…"
                    value={newArtistNotes}
                    onChange={(e) => setNewArtistNotes(e.target.value)}
                    rows={3}
                  />
                  <button
                    onClick={handleAddArtist}
                    disabled={savingArtist}
                    className="w-full px-4 py-2 rounded-lg bg-black text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
                  >
                    {savingArtist ? "Enregistrement…" : "Ajouter"}
                  </button>
                </div>
              </div>

              {/* List artists */}
              <div className="border border-gray-200 rounded-xl p-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900">Artistes suivis</h3>
                  <span className="text-xs text-gray-500">{artists.length}</span>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {loadingArtists ? (
                    <p className="text-xs text-gray-500">Chargement…</p>
                  ) : artists.length === 0 ? (
                    <p className="text-xs text-gray-500">Aucun artiste suivi.</p>
                  ) : (
                    <ul className="space-y-2 text-sm">
                      {artists.map((a) => (
                        <li
                          key={a.id}
                          className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-start justify-between gap-2"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium truncate">{a.artist_name}</span>
                              {a.is_verified && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                  Vérifié
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-gray-500 mt-1">
                              Source: {a.source || "—"} • Score{" "}
                              {(a.confidence_score ?? 0).toFixed(2)}
                            </div>
                            {a.notes && (
                              <div className="text-[11px] text-gray-600 mt-1 whitespace-pre-line">
                                {a.notes}
                              </div>
                            )}
                          </div>

                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <button
                              className="text-[11px] px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                              onClick={() => toggleVerifyArtist(a)}
                            >
                              {a.is_verified ? "Retirer ✓" : "Marquer ✓"}
                            </button>
                            <button
                              className="text-[11px] px-2 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100"
                              onClick={() => deleteArtist(a.id)}
                            >
                              Suppr.
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
